import React, { useEffect, useMemo, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  allPhoneRoutingRulesAtom,
  authenticatedUserAtom,
  phoneRoutingRuleFormAtom,
} from 'state/atoms'
import {
  Table,
  TableBody,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableWrapper,
  SortableTableHeaderCell,
} from 'components/lib/NewTable'
import {
  FlexRow,
  IconButton,
  TableCell,
  TextButton,
  TruncatedText,
} from 'components/lib'
import Search from 'components/lib/Search'
import { orderBy } from 'lodash'
import StyledSelect from 'components/lib/MaterialElements/StyledSelect'
import StyledMenuItem from 'components/lib/MaterialElements/StyledMenuItem'
import { toastError, toastSuccess } from 'components/lib/Toast'
import { Divider, Skeleton } from '@mui/material'
import { MoreHoriz, Phone, Add } from '@mui/icons-material'
import {
  deletePhoneRoutingRule,
  getPhoneRoutingRules,
  type APIPhoneRoutingRule,
} from '../../../api/assistant/phoneRoutingRule'
import PhoneTreeConditionPills from './PhoneTreeConditionPills'

enum SortOption {
  UpdatedAt = 'updatedAt',
  Title = 'title',
  CreatedAt = 'createdAt',
}

const PhoneRoutingRules = () => {
  const [phoneRoutingRules, setPhoneRoutingRules] = useRecoilState<
    APIPhoneRoutingRule[]
  >(allPhoneRoutingRulesAtom)
  const [, setPhoneRoutingRuleForm] = useRecoilState(phoneRoutingRuleFormAtom)
  const [searchText, setSearchText] = useState('')
  const [sortKey, setSortKey] = useState(SortOption.UpdatedAt)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const { selectedContact } = useRecoilValue(authenticatedUserAtom)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchPhoneRoutingRules = async () => {
      if (!selectedContact?.companyId) return
      setIsLoading(true)
      try {
        const rules = await getPhoneRoutingRules(selectedContact.companyId)
        setPhoneRoutingRules(rules)
      } catch (error) {
        toastError('Error fetching phone routing rules')
      } finally {
        setIsLoading(false)
      }
    }

    fetchPhoneRoutingRules()
  }, [selectedContact?.companyId, setPhoneRoutingRules])

  const handleSort = (nextSort: SortOption) => {
    if (nextSort === sortKey) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(nextSort)
      setSortOrder('asc')
    }
  }

  const filteredPhoneRoutingRules = useMemo(
    () =>
      (phoneRoutingRules || []).filter(
        rule =>
          rule.title.toLowerCase().includes(searchText.toLowerCase()) ||
          (rule.description &&
            rule.description.toLowerCase().includes(searchText.toLowerCase())),
      ),
    [searchText, phoneRoutingRules],
  )

  const sortedPhoneRoutingRules = useMemo(() => {
    if (sortKey === SortOption.UpdatedAt) {
      return orderBy(
        filteredPhoneRoutingRules,
        rule => (rule.updatedAt ? rule.updatedAt : rule.createdAt),
        sortOrder,
      )
    }

    if (sortKey === SortOption.Title) {
      return orderBy(filteredPhoneRoutingRules, 'title', sortOrder)
    }

    if (sortKey === SortOption.CreatedAt) {
      return orderBy(filteredPhoneRoutingRules, 'createdAt', sortOrder)
    }

    return filteredPhoneRoutingRules
  }, [sortKey, sortOrder, filteredPhoneRoutingRules])

  const handleDeleteRule = async (ruleId: string) => {
    if (!selectedContact?.companyId) return
    try {
      await deletePhoneRoutingRule(ruleId, selectedContact.companyId)
      setPhoneRoutingRules(prevRules =>
        prevRules.filter(rule => rule.id !== ruleId),
      )
      toastSuccess('Phone routing rule deleted successfully')
    } catch (error) {
      toastError('Failed to delete phone routing rule')
    }
  }

  return (
    <div style={{ marginTop: -40 }}>
      <FlexRow align="center" justify="flex-end">
        <FlexRow>
          <TextButton
            onClick={() => {
              setPhoneRoutingRuleForm({
                phoneRoutingRule: {
                  title: '',
                  description: '',
                },
                isOpen: true,
              })
            }}
          >
            <Add style={{ fontSize: 18 }} />
            <h4 style={{ fontWeight: 500 }}>Add</h4>
          </TextButton>
        </FlexRow>
        <Search
          text={searchText}
          changeText={setSearchText}
          placeholder="Search phone routing rules"
        />
      </FlexRow>

      <TableWrapper>
        <Table>
          <TableHead>
            <TableRow style={{ width: '100%' }}>
              <SortableTableHeaderCell
                active={sortKey === SortOption.Title}
                sortOrder={sortOrder}
                onClick={() => handleSort(SortOption.Title)}
                style={{
                  textAlign: 'left',
                  width: '70%',
                  border: 'none',
                }}
              >
                Title
              </SortableTableHeaderCell>

              <SortableTableHeaderCell
                active={sortKey === SortOption.CreatedAt}
                sortOrder={sortOrder}
                onClick={() => handleSort(SortOption.CreatedAt)}
                style={{
                  textAlign: 'left',
                  width: '20%',
                  border: 'none',
                  outline: 'none',
                }}
              >
                Created At
              </SortableTableHeaderCell>
              <TableHeaderCell
                style={{
                  textAlign: 'left',
                  width: '5%',
                  border: 'none',
                  outline: 'none',
                }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <>
                {[...Array(3)].map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : sortedPhoneRoutingRules &&
              sortedPhoneRoutingRules.length > 0 ? (
              sortedPhoneRoutingRules.map(rule => (
                <TableRow key={rule.id}>
                  <TableCell
                    style={{
                      textTransform: 'none',
                      borderRight: '1px solid #E1E4E8',
                      paddingLeft: 10,
                    }}
                  >
                    <div style={{ marginBottom: 8 }}>
                      <Phone
                        style={{
                          marginRight: 12,
                          fontSize: 18,
                          verticalAlign: 'middle',
                        }}
                      />
                      <span style={{ verticalAlign: 'middle' }}>
                        {rule.title}
                      </span>
                    </div>
                    {rule.phoneTreeConditions &&
                      rule.phoneTreeConditions.length > 0 && (
                        <div style={{ marginLeft: 30 }}>
                          <PhoneTreeConditionPills
                            conditions={rule.phoneTreeConditions}
                          />
                        </div>
                      )}
                  </TableCell>

                  <TableCell
                    style={{
                      textTransform: 'none',
                      borderRight: '1px solid #E1E4E8',
                      paddingLeft: 10,
                    }}
                  >
                    <FlexRow>
                      <TruncatedText>
                        {new Date(rule.createdAt).toLocaleDateString()}
                      </TruncatedText>
                    </FlexRow>
                  </TableCell>
                  <TableCell style={{ textTransform: 'none' }}>
                    <StyledSelect
                      renderValue={() => (
                        <IconButton>
                          <MoreHoriz />
                        </IconButton>
                      )}
                      defaultValue=""
                    >
                      <StyledMenuItem
                        onClick={() => {
                          setPhoneRoutingRuleForm({
                            phoneRoutingRule: rule,
                            isOpen: true,
                          })
                        }}
                      >
                        Edit
                      </StyledMenuItem>

                      <StyledMenuItem
                        onClick={() => {
                          setPhoneRoutingRuleForm({
                            phoneRoutingRule: {
                              title: rule.title,
                              description: rule.description,
                            },
                            isOpen: true,
                          })
                        }}
                      >
                        Duplicate
                      </StyledMenuItem>
                      <Divider />
                      <StyledMenuItem
                        style={{ color: '#d62323' }}
                        onClick={() => handleDeleteRule(rule.id)}
                      >
                        Delete
                      </StyledMenuItem>
                    </StyledSelect>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={3}
                  style={{
                    textTransform: 'none',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: '#627088',
                  }}
                >
                  No phone routing rules to display.{' '}
                  <span
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => {
                      setPhoneRoutingRuleForm({
                        phoneRoutingRule: {
                          title: '',
                          description: '',
                        },
                        isOpen: true,
                      })
                    }}
                  >
                    Add a phone routing rule
                  </span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>
    </div>
  )
}

export default PhoneRoutingRules
